import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Shield,
  Wrench,
  FileText,
  Inbox,
  ScanLine,
  LogOut,
  Stamp,
  Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { fileUrlToFull } from "@/components/ImageUpload";

const NAV = [
  { to: "/", label: "الرئيسية", icon: LayoutDashboard, perm: null, testid: "nav-dashboard" },
  { to: "/employees", label: "الموظفون", icon: Users, perm: "employees.view", testid: "nav-employees" },
  { to: "/roles", label: "الصلاحيات", icon: Shield, perm: "roles.view", testid: "nav-roles" },
  { to: "/form-builder", label: "منشئ النماذج", icon: Wrench, perm: "forms.create", testid: "nav-form-builder" },
  { to: "/forms", label: "تعبئة استمارة", icon: FileText, perm: "submissions.create", testid: "nav-forms" },
  { to: "/submissions", label: "الاستمارات المقدمة", icon: Inbox, perm: "submissions.view_own", testid: "nav-submissions" },
  { to: "/scanner", label: "ماسح QR", icon: ScanLine, perm: "scanner.use", testid: "nav-scanner" },
];

const ADMIN_NAV = [
  { to: "/settings", label: "إعدادات النظام", icon: SettingsIcon, testid: "nav-settings" },
];

export default function DashboardLayout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-surface" dir="rtl">
      <div className="flex min-h-screen">
        {/* Sidebar (right in RTL) */}
        <aside className="w-[280px] bg-card border-s border-border flex flex-col" data-testid="dashboard-sidebar">
          {/* Brand */}
          <div className="px-6 py-5 border-b border-border">
            <div className="flex items-center gap-3">
              {settings?.logo_url ? (
                <img src={fileUrlToFull(settings.logo_url)} alt="logo" className="w-10 h-10 rounded-md object-contain bg-surface border border-border" />
              ) : (
                <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                  <Stamp className="w-5 h-5" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-display font-bold text-lg leading-tight truncate">
                  {settings?.app_name || "إدارة الجوازات"}
                </div>
                <div className="text-xs text-muted-foreground truncate">{settings?.app_subtitle || "نظام رسمي"}</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <div className="px-3 mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              القوائم
            </div>
            <ul className="space-y-0.5 px-2">
              {NAV.filter((n) => !n.perm || hasPermission(n.perm)).map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={item.testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? "nav-active"
                            : "text-foreground/70 hover:bg-surface-hover hover:text-foreground"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
            {user?.is_superadmin && (
              <>
                <div className="px-3 mt-5 mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  مدير النظام
                </div>
                <ul className="space-y-0.5 px-2">
                  {ADMIN_NAV.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          data-testid={item.testid}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                              isActive ? "nav-active" : "text-foreground/70 hover:bg-surface-hover hover:text-foreground"
                            }`
                          }
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </nav>

          {/* User */}
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-primary font-bold text-sm">
                {user?.name?.[0] || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" data-testid="current-user-name">
                  {user?.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {user?.role_names?.join("، ") || "—"}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-sm border border-border hover:bg-surface-hover transition-colors"
            >
              <LogOut className="w-4 h-4" />
              تسجيل الخروج
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-x-hidden">
          <div className="max-w-[1400px] mx-auto px-8 py-8 animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
